import React, { useMemo } from 'react';
import { eventDate, eventTime, extractDate, extractTime } from '../../helper';
import useBootstrapCollapse from '../../util/useBootstrapCollapse';
import {Translate, useTranslation} from '../../config/TranslationProvider';
import EventProblemBadge from '../card/EventProblemBadge';

const FirstEventDateInfo = ({startDate, endDate, sameDayEvents}) => {
    const clock = useTranslation('clock');
    const newDate = useTranslation('newDate');
    const fullDay = useTranslation('full_day');
    return (
        <>
            <ul style={{marginBottom: 20}}>
                <li className="atbd_open">
                    <span style={{fontWeight: 700}}>
                        <Translate value="date" />
                    </span>
                </li>
                <li className="atbd_open">
                    <span>{eventDate(startDate, endDate)}</span>
                </li>
            </ul>

            <ul>
                <li className="atbd_open">
                    <span style={{fontWeight: 700}}>{sameDayEvents.length === 1 ? <Translate value="appointment" /> : <Translate value="appointments" />}</span>
                </li>

                {Object.values(sameDayEvents).map((event, key) =>
                    <ul key={key}>
                        <li className={`atbd_open ${event.is_rescheduled ? 'styles-removed' : ''}`}>
                            <span className="day lh-24 text-left">
                                {extractTime(event.start_date) === extractTime(event.end_date) ?
                                    <Translate value="full_day" />
                                    :
                                    `${eventTime(event.start_date, event.end_date)} ${clock}`
                                }
                            </span>
                            {event.is_cancelled &&
                            <EventProblemBadge type="cancelled" />
                            }
                            {event.is_rescheduled &&
                            <EventProblemBadge type="rescheduled" />
                            }
                        </li>
                        {event.is_rescheduled &&
                        <li className="default-event-list-element mt-1">
                            <span className="rescheduled-color text-left">
                                {event.is_rescheduled && (
                                    event.rescheduled_start_date ?
                                        (
                                            newDate +
                                            eventDate(event.rescheduled_start_date, event.rescheduled_end_date) + ' ' +
                                            (extractTime(event.rescheduled_start_date) === extractTime(event.rescheduled_end_date) ?
                                                fullDay
                                                :
                                                `${eventTime(event.rescheduled_start_date, event.rescheduled_end_date)} ${clock}`)
                                        )
                                        :
                                        <Translate value="newDateUnknown" />
                                )
                                }
                            </span>
                        </li>
                        }
                    </ul>
                )}
            </ul>
        </>
    );
};


const OtherDayEvents = ({otherDayEvents}) => {
    const {collapseRef, collapseSizeRef, showCollapse, toggleCollapse, collapseClassNames} = useBootstrapCollapse();

    const clock = useTranslation('clock');
    const newDate = useTranslation('newDate');
    const fullDay = useTranslation('full_day');
    return <>
        <button
            className="btn btn-secondary"
            type="button"
            aria-label={useTranslation('events_later')}
            aria-expanded={showCollapse ? 'true' : 'false'}
            aria-controls="later-events"
            onClick={toggleCollapse}
        >
            <Translate value="events_later" />
        </button>
        <div
            id="later-events"
            ref={collapseRef}
            className={collapseClassNames}
        >
            <div className="directory_open_hours" ref={collapseSizeRef}>
                <ul className="other-day-event">
                    {Object.values(otherDayEvents).map((event, key) =>
                        <ul key={key}>
                            <li className={'atbd_open'
                                + ((event.is_rescheduled || (event.is_cancelled && key === (otherDayEvents.length - 1))) ? ' styles-removed' : '')
                                + ((!event.is_rescheduled && key !== (otherDayEvents.length - 1)) ? ' default-event-list-element' : '')}
                            >
                                <span className="day lh-24 text-left">
                                    { extractTime(event.start_date) === extractTime(event.end_date) ?
                                        eventDate(event.start_date, event.end_date) + ', ' + fullDay
                                        :
                                        eventDate(event.start_date, event.end_date) + ', '
                                        + eventTime(event.start_date, event.end_date) + ' ' + clock
                                    }
                                </span>
                                {event.is_cancelled &&
                                <EventProblemBadge type="cancelled" />
                                }
                                {event.is_rescheduled &&
                                <EventProblemBadge type="rescheduled" />
                                }
                            </li>
                            {event.is_rescheduled &&
                            <li className={key === (otherDayEvents.length - 1) ?
                                'styles-removed mt-1' : 'default-event-list-element mt-1'}>
                                <span className="rescheduled-color text-left">
                                    {event.is_rescheduled && (
                                        event.rescheduled_start_date ?
                                            (
                                                newDate +
                                                eventDate(event.rescheduled_start_date, event.rescheduled_end_date) + ' ' +
                                                (extractTime(event.rescheduled_start_date) === extractTime(event.rescheduled_end_date) ?
                                                    fullDay
                                                    :
                                                    `${eventTime(event.rescheduled_start_date, event.rescheduled_end_date)} ${clock}`)
                                            )
                                            :
                                            <Translate value="newDateUnknown" />
                                    )
                                    }
                                </span>
                            </li>
                            }
                        </ul>
                    )}
                </ul>
            </div>
        </div>
    </>;
};

const DateInfo = ({eventDates}) => {

    const events = Object.values(eventDates);
    const startDate = events[0] ? events[0].start_date : false;
    const endDate = events[0] ? events[0].end_date : false;

    const {sameDayEvents, otherDayEvents} = useMemo(() => {
        const sameDayEvents = events.filter(event =>
            (extractDate(event.start_date) === extractDate(startDate)) &&
            (extractDate(event.end_date) === extractDate(endDate))
        );
        const otherDayEvents = events.filter(event =>
            (extractDate(event.start_date) !== extractDate(startDate)) ||
            (extractDate(event.end_date) !== extractDate(endDate))
        );

        return {sameDayEvents, otherDayEvents};
    }, [events, startDate, endDate]);

    return (
        <div className="widget atbd_widget widget-card">
            <div className="atbd_widget_title">
                <h4><span className="la la-clock-o" />&nbsp;<Translate value="date_info" /></h4>
            </div>
            <div className="directory_open_hours">
                {startDate !== false && endDate !== false && (
                    <FirstEventDateInfo
                        startDate={startDate}
                        endDate={endDate}
                        sameDayEvents={sameDayEvents}
                    />
                )}
                {(startDate === false || endDate === false) && (
                    <Translate value="no_future_dates" />
                )}
            </div>

            {otherDayEvents.length > 0 && (
                <OtherDayEvents otherDayEvents={otherDayEvents} />
            )}
        </div>
    );
};

export default DateInfo;
